import { useMemo, useState } from "react";
import { BookOpen, Check, Copy, FlaskConical, Plus, Send, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


const inputCls =
    "rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none";

const METHOD_TONE = {
    get: "text-emerald-300 border-emerald-500/30 bg-emerald-950/40",
    post: "text-cyan-300 border-cyan-500/30 bg-cyan-950/40",
    put: "text-amber-300 border-amber-500/30 bg-amber-950/40",
    patch: "text-amber-300 border-amber-500/30 bg-amber-950/40",
    delete: "text-rose-300 border-rose-500/30 bg-rose-950/40",
};

export default function DevelopPanel({ appId, app, onChanged }) {
    const [name, setName] = useState(app?.name || "");
    const [description, setDescription] = useState(app?.description || "");
    const [newType, setNewType] = useState("");
    const [evtType, setEvtType] = useState("");
    const [evtData, setEvtData] = useState('{\n  "id": "ord_123",\n  "amount": 4200\n}');
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const spec = useMemo(() => {
        try {
            return JSON.parse(app?.spec || "{}");
        } catch {
            return {};
        }
    }, [app?.spec]);

    const endpoints = useMemo(() => {
        const out = [];
        for (const [path, ops] of Object.entries(spec.paths || {})) {
            for (const [method, op] of Object.entries(ops || {})) {
                out.push({ method: method.toUpperCase(), path, summary: op?.summary || "" });
            }
        }
        return out;
    }, [spec]);

    const eventTypes = Object.keys(spec["x-webhooks"] || {});
    const ingestUrl = `${BACKEND}/api/ingest/${appId}/events`;
    const sampleType = eventTypes[0] || "order.created";
    const SNIPPETS = {
        curl: `curl -X POST ${ingestUrl} \\
  -H "Authorization: Bearer <your publish key>" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"${sampleType}","data":{"id":"ord_123"}}'`,
        node: `// Node.js 18+ — no dependencies
const resp = await fetch("${ingestUrl}", {
  method: "POST",
  headers: {
    Authorization: "Bearer <your publish key>",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    type: "${sampleType}",
    data: { id: "ord_123" },
  }),
});
console.log(await resp.json()); // { id: "evt_…", received: true }`,
        python: `import requests

resp = requests.post(
    "${ingestUrl}",
    headers={"Authorization": "Bearer <your publish key>"},
    json={"type": "${sampleType}", "data": {"id": "ord_123"}},
)
print(resp.json())  # {"id": "evt_…", "received": True}`,
    };
    const [lang, setLang] = useState("curl");

    const copySnippet = async () => {
        try {
            await navigator.clipboard.writeText(SNIPPETS[lang]);
            setCopied(true);
            toast.success("Snippet copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    const saveConfig = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/apps/${appId}`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: name.trim(), description: description.trim() }),
            });
            if (!r.ok) throw new Error("failed");
            toast.success("App settings saved");
            onChanged?.();
        } catch {
            toast.error("Could not save settings");
        } finally {
            setBusy(false);
        }
    };

    const addType = async () => {
        const t = newType.trim().toLowerCase();
        if (!t) return;
        try {
            const r = await fetch(`${API}/apps/${appId}/event-types`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: t }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setNewType("");
            toast.success(`Event type "${t}" added`);
            onChanged?.();
        } catch (e) {
            toast.error("Could not add event type", { description: String(e.message) });
        }
    };

    const removeType = async (t) => {
        try {
            const r = await fetch(`${API}/apps/${appId}/event-types/${t}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            toast.success(`Event type "${t}" removed`);
            onChanged?.();
        } catch {
            toast.error("Could not remove event type");
        }
    };

    const publishTest = async () => {
        const t = evtType.trim() || eventTypes[0];
        if (!t) {
            toast.error("Pick or type an event type first");
            return;
        }
        let data = {};
        try {
            data = JSON.parse(evtData || "{}");
        } catch {
            toast.error("Payload is not valid JSON");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/events/test`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: t, data }),
            });
            if (!r.ok) throw new Error("failed");
            toast.success("Event created", {
                description: "Watch it land in the live stream above.",
            });
        } catch {
            toast.error("Could not create event");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="develop-panel" className="mt-4 space-y-4">
            <div className="glass-panel rounded-xl p-5">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <BookOpen className="h-4 w-4 text-cyan-400" />
                    Develop this API
                </h3>
                <ol className="mt-4 space-y-2.5 font-mono text-[11px] leading-relaxed text-slate-400">
                    <li>
                        <span className="text-slate-600">1 ·</span> Create a{" "}
                        <span className="text-cyan-300">publish</span> or{" "}
                        <span className="text-cyan-300">admin</span> key in the API keys panel
                        below.
                    </li>
                    <li>
                        <span className="text-slate-600">2 ·</span> Send events from anywhere with
                        one POST — they appear in your live stream instantly:
                    </li>
                </ol>
                <div className="relative mt-3 rounded-lg border border-slate-800 bg-slate-950/80">
                    <div className="flex items-center justify-between border-b border-slate-800/80 px-3 py-1.5">
                        <div className="flex items-center gap-1">
                            {[
                                ["curl", "cURL"],
                                ["node", "Node.js"],
                                ["python", "Python"],
                            ].map(([id, label]) => (
                                <button
                                    key={id}
                                    data-testid={`develop-snippet-tab-${id}`}
                                    onClick={() => setLang(id)}
                                    className={`rounded px-2.5 py-1 font-mono text-[10px] transition-colors ${
                                        lang === id
                                            ? "bg-cyan-950/70 text-cyan-300"
                                            : "text-slate-500 hover:text-slate-300"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="develop-copy-curl"
                            onClick={copySnippet}
                            className="flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900/80 px-2.5 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                            {copied ? "copied" : "copy"}
                        </button>
                    </div>
                    <pre
                        data-testid="develop-curl-snippet"
                        className="overflow-x-auto whitespace-pre-wrap p-4 font-mono text-[11px] leading-relaxed text-cyan-100"
                    >
                        {SNIPPETS[lang]}
                    </pre>
                </div>
                <p className="mt-3 font-mono text-[10px] leading-relaxed text-slate-500">
                    3 · Build your resources against the endpoints below — your spec defines the
                    contract; MDH owns event delivery, retries, and observability.
                </p>
            </div>

            <div className="glass-panel rounded-xl p-5">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <FlaskConical className="h-4 w-4 text-teal-400" />
                    Create an event
                </h3>
                <div className="mt-4 grid gap-2 sm:grid-cols-[220px_1fr_auto]">
                    <input
                        data-testid="test-event-type-input"
                        value={evtType}
                        onChange={(e) => setEvtType(e.target.value)}
                        placeholder={eventTypes[0] || "event.type"}
                        list={`event-types-${appId}`}
                        className={inputCls}
                    />
                    <datalist id={`event-types-${appId}`}>
                        {eventTypes.map((t) => (
                            <option key={t} value={t} />
                        ))}
                    </datalist>
                    <textarea
                        data-testid="test-event-data-input"
                        value={evtData}
                        onChange={(e) => setEvtData(e.target.value)}
                        rows={2}
                        className={inputCls}
                    />
                    <button
                        data-testid="test-event-publish-button"
                        onClick={publishTest}
                        disabled={busy}
                        className="flex items-center gap-1.5 self-start rounded-md bg-cyan-400 px-4 py-2 font-mono text-[11px] font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                    >
                        <Send className="h-3 w-3" /> Publish
                    </button>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="glass-panel rounded-xl p-5">
                    <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                        Endpoints
                    </h3>
                    <div className="mt-3 space-y-1.5">
                        {endpoints.length === 0 && (
                            <p className="rounded-lg border border-dashed border-slate-800 py-6 text-center font-mono text-[11px] text-slate-600">
                                No endpoints in the spec yet.
                            </p>
                        )}
                        {endpoints.map((e) => (
                            <div
                                key={`${e.method}-${e.path}`}
                                data-testid={`develop-endpoint-${e.method}-${e.path.replace(/[^a-z0-9]/gi, "-")}`}
                                className="flex items-center gap-2.5 rounded-lg border border-slate-800/80 bg-slate-950/50 px-3.5 py-2 font-mono text-[11px]"
                            >
                                <span
                                    className={`rounded border px-1.5 py-px text-[9px] font-bold ${
                                        METHOD_TONE[e.method.toLowerCase()] || METHOD_TONE.get
                                    }`}
                                >
                                    {e.method}
                                </span>
                                <span className="text-slate-200">{e.path}</span>
                                <span className="ml-auto hidden truncate text-[10px] text-slate-600 sm:inline">
                                    {e.summary}
                                </span>
                            </div>
                        ))}
                    </div>
                    <h4 className="mt-5 font-display text-sm font-bold tracking-tight text-slate-100">
                        Event types
                    </h4>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {eventTypes.map((t) => (
                            <span
                                key={t}
                                data-testid={`develop-event-type-${t}`}
                                className="group flex items-center gap-1.5 rounded border border-teal-500/30 bg-teal-950/40 px-2 py-1 font-mono text-[10px] text-teal-300"
                            >
                                {t}
                                <button
                                    data-testid={`develop-event-type-remove-${t}`}
                                    onClick={() => removeType(t)}
                                    className="text-teal-600 transition-colors hover:text-rose-300"
                                    aria-label={`Remove ${t}`}
                                >
                                    <X className="h-2.5 w-2.5" />
                                </button>
                            </span>
                        ))}
                        {eventTypes.length === 0 && (
                            <span className="font-mono text-[10px] text-slate-600">
                                none defined — add your first below
                            </span>
                        )}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <input
                            data-testid="event-type-add-input"
                            value={newType}
                            onChange={(e) => setNewType(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addType()}
                            placeholder="e.g. payment.failed"
                            className={`${inputCls} min-w-0 flex-1`}
                        />
                        <button
                            data-testid="event-type-add-button"
                            onClick={addType}
                            disabled={!newType.trim()}
                            className="flex items-center gap-1.5 rounded-md border border-teal-500/40 bg-teal-950/40 px-3.5 py-2 font-mono text-[11px] font-semibold text-teal-300 transition-colors hover:bg-teal-950/70 disabled:opacity-50"
                        >
                            <Plus className="h-3 w-3" /> Add
                        </button>
                    </div>
                </div>

                <div className="glass-panel rounded-xl p-5">
                    <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                        <Settings2 className="h-4 w-4 text-cyan-400" />
                        Configure app
                    </h3>
                    <div className="mt-4 grid gap-3">
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Name
                            </span>
                            <input
                                data-testid="app-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={`${inputCls} w-full`}
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Description
                            </span>
                            <textarea
                                data-testid="app-description-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                                placeholder="What does this API do?"
                                className={`${inputCls} w-full`}
                            />
                        </label>
                        <button
                            data-testid="app-config-save-button"
                            onClick={saveConfig}
                            disabled={busy || !name.trim()}
                            className="w-fit rounded-md bg-cyan-400 px-5 py-2 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                        >
                            {busy ? "Saving…" : "Save settings"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
